/**
 * Maths in text from outside the app, typeset by KaTeX (#68, #87): the
 * agent's replies and notes (Markdown), and an article's reader view (HTML).
 * **Class C**: how text is drawn, not what is shown.
 *
 * The delimiters are the ones a model writes and the ones the sites write for
 * MathJax (Quanta: `$latex …$` and `\(…\)` inline, `$$…$$` and `\[…\]`
 * display). A single `$` is maths only as Pandoc reads it: a non-space after
 * the opening one, a non-space before the closing one, and no digit after it,
 * so "$5 and $10" stays money. `\$` is a dollar. LaTeX that does not parse
 * shows its source, as in Atlas (`widgets/atlas/cardText.ts`).
 *
 * Markdown has its maths taken out before `marked` reads it, because `marked`
 * reads `\(` as an escaped `(` and `_` as emphasis; code spans and blocks keep
 * theirs. HTML has its maths found in its text, after it is made safe, and
 * never in `code` or `pre`. KaTeX's own output (`trust` off) goes in after
 * DOMPurify: it is made from the source's characters, escaped.
 *
 * KaTeX is loaded the first time a text has maths in it.
 */

type Katex = typeof import("katex").default;

let katex: Katex | null = null;
let loading: Promise<Katex> | null = null;

/** KaTeX and its styles, loaded once. */
export function loadKatex(): Promise<Katex> {
  loading ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")]).then(([m]) => {
    katex = m.default;
    return katex;
  });
  return loading;
}

/** KaTeX if it has loaded. */
export function loadedKatex(): Katex | null {
  return katex;
}

/** Display `$$…$$` and `\[…\]`; inline `\(…\)`, `$latex …$` and `$…$`. */
const MATHS =
  /(?<!\\)\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|(?<!\\)\$latex\s([^$]+?)\$|(?<![\\$])\$(?=[^\s$])([^$\n]*?[^\s$\\])\$(?![\d$])/g;

/** A code block or span in Markdown, whose dollars are not maths. */
const CODE = /```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]+`/g;

const SKIP = new Set(["CODE", "PRE", "KBD", "SAMP", "SCRIPT", "STYLE", "TEXTAREA"]);

interface Piece {
  tex: string;
  display: boolean;
}

export function hasMaths(text: string): boolean {
  return new RegExp(MATHS.source).test(text.replace(CODE, ""));
}

function piece(
  dollars?: string,
  bracket?: string,
  paren?: string,
  latex?: string,
  single?: string,
): Piece {
  if (dollars !== undefined) return { tex: dollars, display: true };
  if (bracket !== undefined) return { tex: bracket, display: true };
  return { tex: paren ?? latex ?? single ?? "", display: false };
}

const escape = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** One piece as HTML: KaTeX's, or its source when KaTeX cannot read it or is not loaded. */
function typesetPiece(p: Piece, source: string, render: Katex | null): string {
  if (!render) return escape(source);
  try {
    return render.renderToString(p.tex, {
      displayMode: p.display,
      throwOnError: true,
      output: "html",
      trust: false,
    });
  } catch {
    return escape(source);
  }
}

/** A mark `marked` passes as text and that no reply contains. */
const mark = (i: number) => `KINDREDMATHS${i}X`;
const MARKS = /KINDREDMATHS(\d+)X/g;

/**
 * Markdown with its maths taken out, for `marked`, and a function that puts
 * the maths back into the HTML that comes out.
 */
export function holdMaths(markdown: string): {
  text: string;
  restore: (html: string, render: Katex | null) => string;
} {
  const held: { p: Piece; source: string }[] = [];
  let text = "";
  let last = 0;
  for (const code of [...markdown.matchAll(CODE), null]) {
    const end = code ? (code.index ?? 0) : markdown.length;
    text += markdown
      .slice(last, end)
      .replace(
        MATHS,
        (
          whole: string,
          dollars?: string,
          bracket?: string,
          paren?: string,
          latex?: string,
          single?: string,
        ) => {
          held.push({ p: piece(dollars, bracket, paren, latex, single), source: whole });
          return mark(held.length - 1);
        },
      );
    if (code) text += code[0];
    last = end + (code ? code[0].length : 0);
  }
  return {
    text,
    restore: (html, render) =>
      html.replace(MARKS, (whole, i: string) => {
        const h = held[Number(i)];
        return h ? typesetPiece(h.p, h.source, render) : whole;
      }),
  };
}

/** Safe HTML with the maths in its text typeset, outside code. */
export function typesetHtml(html: string, render: Katex | null): string {
  if (!render) return html;
  const box = document.createElement("template");
  box.innerHTML = html;
  const walker = document.createTreeWalker(box.content, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      for (let el = node.parentElement; el; el = el.parentElement)
        if (SKIP.has(el.tagName)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const texts: Text[] = [];
  while (walker.nextNode()) texts.push(walker.currentNode as Text);
  for (const node of texts) {
    const value = node.data;
    if (!new RegExp(MATHS.source).test(value)) continue;
    let out = "";
    let last = 0;
    for (const m of value.matchAll(MATHS)) {
      out += escape(value.slice(last, m.index));
      out += typesetPiece(piece(m[1], m[2], m[3], m[4], m[5]), m[0], render);
      last = (m.index ?? 0) + m[0].length;
    }
    out += escape(value.slice(last));
    const span = document.createElement("template");
    span.innerHTML = out;
    node.replaceWith(span.content);
  }
  return box.innerHTML;
}
